import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Check, Mail, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import {
  getPublicUnsubscribeGroupsRequest,
  getUnsubscribePreferenceRequest,
  createUnsubscribePreferenceRequest,
  updateUnsubscribePreferenceRequest,
  clearUnsubscribePreferenceStatus,
} from 'store/unsubscribePreference';
import {
  selectUnsubscribeGroups,
  selectUnsubscribePreferenceLoading,
  selectUnsubscribePreferenceError,
  selectUnsubscribePreference,
  selectUnsubscribePreferenceSuccess,
} from 'store/unsubscribePreference/unsubscribePreference.selector';
import { IUnsubscribeGroup } from 'types/unsubscribeGroups/unsubscribeGroups.types';
import { useToast } from 'contexts/ToastContext';

const UnsubscribePreferenceMail: React.FC = () => {
  const { trackingId } = useParams<{ trackingId: string }>();

  const dispatch = useDispatch();
  const unsubscribeGroups = useSelector(selectUnsubscribeGroups);
  const preference = useSelector(selectUnsubscribePreference);
  const loading = useSelector(selectUnsubscribePreferenceLoading);
  const error = useSelector(selectUnsubscribePreferenceError);
  const success = useSelector(selectUnsubscribePreferenceSuccess);
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [unsubscribeAll, setUnsubscribeAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (trackingId) {
      dispatch(getPublicUnsubscribeGroupsRequest(trackingId));
      dispatch(getUnsubscribePreferenceRequest(trackingId));
    }
  }, [trackingId, dispatch]);

  useEffect(() => {
    if (preference) {
      setUnsubscribeAll(preference.unsubscribeAll || false);
      setSelectedGroups(new Set(preference.unsubscribeGroups || []));
    } else {
      setUnsubscribeAll(false);
      const groupIds = unsubscribeGroups
        .filter((group: IUnsubscribeGroup) => group.isDefault)
        .map((group: IUnsubscribeGroup) => group._id || '');
      setSelectedGroups(new Set(groupIds));
    }
  }, [preference, unsubscribeGroups]);

  useEffect(() => {
    if (isSubmitting && !loading && trackingId) {
      if (error) {
        setIsSubmitting(false);
        showToast({
          type: 'error',
          title: 'Error',
          message: error,
          duration: 5000,
        });
        dispatch(clearUnsubscribePreferenceStatus());
      } else if (success) {
        setIsSubmitting(false);
        showToast({
          type: 'success',
          title: 'Success',
          message: 'Your email preferences have been updated successfully!',
          duration: 5000,
        });
        dispatch(clearUnsubscribePreferenceStatus());
        dispatch(getUnsubscribePreferenceRequest(trackingId));
      }
    }
  }, [loading, error, success, isSubmitting, showToast, dispatch, trackingId]);

  const handleGroupToggle = (groupId: string) => {
    const newSelected = new Set(selectedGroups);
    if (newSelected.has(groupId)) {
      newSelected.delete(groupId);
    } else {
      newSelected.add(groupId);
    }
    setSelectedGroups(newSelected);
    setUnsubscribeAll(false);
  };

  const handleUnsubscribeAllToggle = () => {
    setUnsubscribeAll(!unsubscribeAll);
    if (!unsubscribeAll) {
      setSelectedGroups(new Set());
    }
  };

  const handleConfirmPreferences = () => {
    if (!trackingId) return;

    const unsubscribeData = {
      unsubscribeAll: unsubscribeAll,
      unsubscribeGroups: Array.from(selectedGroups),
    };

    setIsSubmitting(true);

    if (preference?._id) {
      dispatch(updateUnsubscribePreferenceRequest(trackingId, unsubscribeData));
    } else {
      dispatch(createUnsubscribePreferenceRequest(trackingId, unsubscribeData));
    }
  };

  const Footer = () => (
    <footer className="mt-8 text-center">
      <div className="inline-flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-4 py-3 bg-card/80 backdrop-blur-md rounded-2xl shadow-md border border-border">
        <p className="text-xs text-muted-foreground font-medium">
          © 2025 SegMailer Inc.
        </p>
        <div className="flex items-center gap-4 text-xs">
          <a
            href="/legal"
            className="text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Legal
          </a>
          <a
            href="/privacy"
            className="text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Privacy
          </a>
          <a
            href="/cookies"
            className="text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Manage cookies
          </a>
        </div>
      </div>
    </footer>
  );

  if (!trackingId || trackingId === 'null') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-primary-background to-info-background flex items-center justify-center py-6 sm:py-8 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-info/15 rounded-full blur-3xl" />
        </div>
        <div className="max-w-xl w-full mx-auto px-4 relative z-10">
          <div className="bg-card/95 backdrop-blur-md rounded-2xl shadow-xl border border-destructive/30 p-8 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-destructive rounded-2xl mb-5 shadow-lg">
              <AlertTriangle
                className="w-7 h-7 text-destructive-foreground"
                strokeWidth={2.5}
                aria-hidden
              />
            </div>
            <h1 className="text-2xl font-bold text-foreground mb-3">
              Invalid Request
            </h1>
            <p className="text-muted-foreground mb-6">
              The tracking ID is missing or invalid. Please check the link and
              try again.
            </p>
            <p className="text-sm text-muted-foreground">
              If you believe this is an error, please contact support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !preference && unsubscribeGroups.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-primary-background to-info-background flex items-center justify-center py-6 sm:py-8 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-info/15 rounded-full blur-3xl" />
        </div>
        <div className="max-w-xl w-full mx-auto px-4 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-2xl mb-6 shadow-lg">
            <div
              className="animate-spin rounded-full h-7 w-7 border-2 border-primary-foreground border-t-transparent"
              aria-hidden
            />
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            Loading your preferences…
          </p>
          <Footer />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary-background to-info-background flex items-center justify-center py-6 sm:py-8 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-info/15 rounded-full blur-3xl" />
      </div>
      <div className="max-w-xl w-full mx-auto px-4 relative z-10">
        <header className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-14 h-14 bg-primary rounded-2xl mb-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            aria-hidden
          >
            <Mail
              className="w-7 h-7 text-primary-foreground"
              strokeWidth={2.5}
            />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 tracking-tight">
            Update Your Email Preferences
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Choose which types of emails you'd like to receive
          </p>
        </header>

        <main className="bg-card/95 backdrop-blur-md rounded-2xl shadow-xl border border-border p-6 sm:p-7 relative overflow-hidden mb-8 max-w-md mx-auto">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-background/40 via-transparent to-info-background/40 pointer-events-none rounded-2xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 px-3 py-2 bg-info-background rounded-xl mb-5">
              <Info className="w-4 h-4 text-info flex-shrink-0" aria-hidden />
              <p className="text-xs font-medium text-foreground">
                Select which types of emails you'd like to receive from us
              </p>
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-10">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-primary rounded-2xl mb-4 shadow-lg">
                    <div
                      className="animate-spin rounded-full h-6 w-6 border-2 border-primary-foreground border-t-transparent"
                      aria-hidden
                    />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Loading your preferences…
                  </p>
                </div>
              ) : error ? (
                <div className="text-center py-10">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-destructive-background rounded-2xl mb-4 shadow-md">
                    <AlertCircle
                      className="w-6 h-6 text-destructive"
                      aria-hidden
                    />
                  </div>
                  <p className="text-sm font-medium text-destructive">
                    Error: {error}
                  </p>
                </div>
              ) : (
                <>
                  {unsubscribeGroups.map(
                    (group: IUnsubscribeGroup, index: number) => (
                      <section
                        key={group._id}
                        className="group relative overflow-hidden rounded-xl border border-border bg-card/80 hover:border-primary/40 hover:shadow-md transition-all duration-300"
                        style={{ animationDelay: `${index * 50}ms` }}
                        aria-labelledby={`group-${group._id}`}
                      >
                        {selectedGroups.has(group._id || '') && (
                          <div
                            className="absolute top-0 left-0 w-1 h-full bg-primary rounded-l-xl"
                            aria-hidden
                          />
                        )}

                        <div className="flex items-start p-4">
                          <div className="flex-shrink-0 mt-1">
                            <input
                              type="checkbox"
                              id={group._id}
                              checked={selectedGroups.has(group._id || '')}
                              onChange={() =>
                                handleGroupToggle(group._id || '')
                              }
                              className="sr-only"
                              aria-describedby={`group-desc-${group._id}`}
                            />
                            <label
                              htmlFor={group._id}
                              className={`flex items-center justify-center w-5 h-5 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                                selectedGroups.has(group._id || '')
                                  ? 'bg-primary border-primary shadow-md'
                                  : 'border-input hover:border-primary/50 hover:bg-primary-background'
                              }`}
                            >
                              {selectedGroups.has(group._id || '') && (
                                <Check
                                  className="w-3 h-3 text-primary-foreground"
                                  aria-hidden
                                />
                              )}
                            </label>
                          </div>
                          <div className="flex-1 ml-4 min-w-0">
                            <label
                              htmlFor={group._id}
                              className="block cursor-pointer"
                            >
                              <span
                                id={`group-${group._id}`}
                                className="text-sm font-semibold text-foreground mb-1 block group-hover:text-primary transition-colors duration-200"
                              >
                                {group.title}
                              </span>
                              <span
                                id={`group-desc-${group._id}`}
                                className="text-xs text-muted-foreground leading-relaxed block group-hover:text-foreground/80 transition-colors duration-200"
                              >
                                {group.description}
                              </span>
                            </label>
                          </div>
                        </div>
                      </section>
                    )
                  )}

                  <div className="mt-6 pt-6 border-t border-border">
                    <section
                      className="group relative overflow-hidden rounded-xl border border-destructive/30 bg-destructive-background/80 hover:border-destructive/50 transition-all duration-300"
                      aria-labelledby="unsubscribe-all-heading"
                    >
                      {unsubscribeAll && (
                        <div
                          className="absolute top-0 left-0 w-1 h-full bg-destructive rounded-l-xl"
                          aria-hidden
                        />
                      )}

                      <div className="flex items-start p-4">
                        <div className="flex-shrink-0 mt-1">
                          <input
                            type="checkbox"
                            id="unsubscribe-all"
                            checked={unsubscribeAll}
                            onChange={handleUnsubscribeAllToggle}
                            className="sr-only"
                            aria-describedby="unsubscribe-all-desc"
                          />
                          <label
                            htmlFor="unsubscribe-all"
                            className={`flex items-center justify-center w-5 h-5 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              unsubscribeAll
                                ? 'bg-destructive border-destructive shadow-md'
                                : 'border-destructive hover:border-destructive-hover hover:bg-destructive-background'
                            }`}
                          >
                            {unsubscribeAll && (
                              <Check
                                className="w-3 h-3 text-destructive-foreground"
                                aria-hidden
                              />
                            )}
                          </label>
                        </div>
                        <div className="flex-1 ml-4 min-w-0">
                          <label
                            htmlFor="unsubscribe-all"
                            className="block cursor-pointer"
                          >
                            <span
                              id="unsubscribe-all-heading"
                              className="text-sm font-semibold text-destructive mb-1 flex items-center gap-2 group-hover:text-destructive-hover transition-colors duration-200"
                            >
                              <AlertTriangle
                                className="w-4 h-4 flex-shrink-0"
                                aria-hidden
                              />
                              Unsubscribe from all lists
                            </span>
                            <span
                              id="unsubscribe-all-desc"
                              className="text-xs text-muted-foreground leading-relaxed block group-hover:text-foreground/80 transition-colors duration-200"
                            >
                              No longer receive any emails, except for important
                              account notifications.
                            </span>
                          </label>
                        </div>
                      </div>
                    </section>
                  </div>
                </>
              )}
            </div>

            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={handleConfirmPreferences}
                disabled={
                  loading ||
                  isSubmitting ||
                  (selectedGroups.size === 0 &&
                    !unsubscribeAll &&
                    !preference?._id)
                }
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-primary-foreground bg-primary rounded-xl shadow-md hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-all duration-200"
              >
                {isSubmitting ? (
                  <>
                    <div
                      className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent"
                      aria-hidden
                    />
                    Updating…
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" aria-hidden />
                    Confirm Email Preferences
                  </>
                )}
              </button>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default UnsubscribePreferenceMail;
